/* eslint-disable no-await-in-loop */
/* eslint-disable no-use-before-define */
import React, { useState, useMemo } from 'react';
import { useFormik, FormikProvider } from 'formik';
import * as yup from 'yup';
import gql from 'graphql-tag';
import { FaArrowLeft } from 'react-icons/fa';
import { useMutation, useQuery } from 'react-apollo';
import propTypes from 'prop-types';
import classnames from 'classnames';
import SuccessFeedback from './AssemblyPutSuccess';
import TextArea from '../../../components/TextArea';
import AssemblySubjects from './AssemblySubjects';
import Downloads from './AssemblyDownloads';
import useToast from '../../../hooks/useToast';

const optionSchema = yup.object({
  description: yup
    .string()
    .required()
    .default(''),
});

const subjectSchema = yup.object({
  _id: yup.string().nullable(),
  title: yup
    .string()
    .required()
    .default(''),
  description: yup
    .string()
    .required()
    .default(''),
  hasVotes: yup.bool().default(true),
  attachments: yup
    .array()
    .of(yup.mixed())
    .default([]),
  options: yup
    .array()
    .default([{ description: 'Sim' }, { description: 'Não' }])
    .when('hasVotes', hasVotes =>
      hasVotes
        ? yup
            .array()
            .of(optionSchema)
            .required()
        : yup.array(),
    ),
});

const assemblySchema = yup.object({
  _id: yup.string().nullable(),
  description: yup
    .string()
    .required()
    .default(''),
  condominium: yup.string().required(),
  type: yup
    .string()
    .oneOf(['extraordinary', 'ordinary', 'meeting'])
    .default('ordinary'),
  status: yup.string().nullable(),
  subjects: yup
    .array()
    .of(subjectSchema)
    .default([subjectSchema.default()])
    .required(),
  attachments: yup
    .array()
    .of(yup.mixed())
    .default([]),
});

const registerGql = gql`
  mutation register($assembly: AssemblyRegister!) {
    createAssembly(assembly: $assembly) {
      _id
      description
      condominium
      subjects {
        _id
      }
    }
  }
`;

const updateGql = gql`
  mutation update($assembly: AssemblyRegister!) {
    updateAssembly(assembly: $assembly) {
      _id
      description
      condominium
      attachments {
        _id
        name
      }
      subjects {
        attachments {
          _id
          name
        }
        _id
      }
    }
  }
`;

const optionDescriptionAssemblyPutFragment = gql`
  fragment optionDescriptionAssemblyPutFragment on Option {
    description
  }
`;

const getGql = gql`
  query assembly($_id: ID!) {
    assembly(_id: $_id) {
      _id
      description
      condominium
      type
      status
      attachments {
        _id
        name
        path
      }
      subjects {
        _id
        title
        description
        options {
          ...optionDescriptionAssemblyPutFragment
        }
        attachments {
          _id
          name
          path
        }
      }
    }
  }
  ${optionDescriptionAssemblyPutFragment}
`;

const uploadGql = gql`
  mutation upload($assemblyId: ID!, $condominiumId: ID!, $subjectId: ID, $files: [Upload!]!) {
    assemblyAttachment(assemblyId: $assemblyId, condominiumId: $condominiumId, subjectId: $subjectId, files: $files)
  }
`;

export default function AssemblyPut({ match, history }) {
  const [isModalVisible, setModalVisible] = useState(false);
  const title = match.params.type === 'meeting' ? 'Reunião' : 'Assembleia';
  const isEdit = useMemo(() => !!match.params.assemblyId, [match]);
  const toast = useToast();
  const [upload] = useMutation(uploadGql, {
    onCompleted: () => toast.fire({ icon: 'success', text: 'Upload concluído!' }),
  });
  const onCompleted = async assembly => {
    const assemblyAttachments = values.attachments.filter(attach => !attach._id);
    let warned = false;

    const warn = () => {
      if (warned) return;

      warned = true;
      toast.fire({ icon: 'warning', text: 'Ainda estamos fazendo o upload..' });
    };

    if (assemblyAttachments.length) {
      warn();
      await upload({
        variables: {
          condominiumId: assembly.condominium,
          assemblyId: assembly._id,
          files: assemblyAttachments,
        },
      });
    }

    for (let i = 0, l = assembly.subjects.length; i < l; i += 1) {
      const subjectAttachments = values.subjects[i].attachments.filter(attach => !attach._id);

      if (subjectAttachments.length) {
        warn();
        await upload({
          variables: {
            assemblyId: assembly._id,
            condominiumId: assembly.condominium,
            subjectId: assembly.subjects[i]._id,
            files: subjectAttachments,
          },
        });
      }
    }

    setModalVisible(true);
  };

  const [registerFn, { data: registerData }] = useMutation(registerGql, {
    onCompleted: ({ createAssembly }) => {
      onCompleted(createAssembly);
      setModalVisible(true);
    },
  });
  const [updateFn, { data: updateData }] = useMutation(updateGql, {
    onCompleted: ({ updateAssembly }) => {
      onCompleted(updateAssembly);
      setModalVisible(true);
    },
  });

  const assemblyId = useMemo(
    () =>
      (registerData && registerData.createAssembly && registerData.createAssembly._id) ||
      (updateData && updateData.updateAssembly && updateData.updateAssembly._id),
    [updateData, registerData],
  );

  const formikBag = useFormik({
    validationSchema: assemblySchema,
    initialValues: {
      ...assemblySchema.default(),
      type: (match.params.type === 'meeting' && match.params.type) || assemblySchema.default().type,
      condominium: match.params.condominiumId,
      _id: match.params.assemblyId,
    },
    onSubmit: values => {
      const { attachments: _, ...entity } = assemblySchema.cast(values, {
        stripUnknown: true,
      });
      const args = {
        variables: {
          assembly: {
            ...entity,
            attachments: _.filter(x => !!x._id).map(({ __typename, ...x }) => x),
            subjects: entity.subjects.map(({ hasVotes, attachments, ...x }) => ({
              ...x,
              attachments: attachments.filter(a => !!a._id).map(({ __typename, ...a }) => a),
              options: hasVotes ? x.options : [],
            })),
          },
        },
      };
      if (values._id) return updateFn(args);
      return registerFn(args);
    },
  });

  useQuery(getGql, {
    fetchPolicy: 'network-only',
    skip: !match.params.condominiumId || !match.params.assemblyId,
    variables: {
      _id: match.params.assemblyId,
    },
    onCompleted: ({ assembly }) => {
      formikBag.setValues({
        ...assembly,
        subjects: assembly.subjects.map(x => ({
          ...x,
          hasVotes: !!x.options.length,
        })),
      });
    },
  });

  const { values, handleChange, handleSubmit, isSubmitting, errors, touched } = formikBag;

  const buttonLabel = useMemo(() => {
    if (isEdit) {
      return isSubmitting ? `Editando ${title.toLowerCase()}` : `Editar ${title.toLowerCase()}`;
    }
    return isSubmitting ? `Criando ${title.toLowerCase()}` : `Criar ${title.toLowerCase()}`;
  }, [isEdit, isSubmitting]);

  return (
    <FormikProvider value={formikBag}>
      <>
        <FaArrowLeft onClick={() => history.push('/dashboard')} className="mt-3 back-icon" />
        {isModalVisible && <SuccessFeedback isEdit={isEdit} assembly={{ ...values, _id: assemblyId }} />}
        <div className="h-100 w-100 d-flex flex-column">
          <div className="justify-content-center d-flex p-3">
            <h4>
              {isEdit ? 'Edição de ' : 'Nova '}
              {title}
            </h4>
          </div>
          <form className="form" onSubmit={handleSubmit}>
            {match.params.type !== 'meeting' && (
              <div className="form-group">
                <label>Tipo</label>
                <select className="form-control form-control" name="type" onChange={handleChange} value={values.type}>
                  <option value="ordinary">Ordinária</option>
                  <option value="extraordinary">Extraordinária</option>
                </select>
              </div>
            )}
            <div className="form-group">
              <label htmlFor="title">Descrição</label>
              <TextArea
                sm
                value={values.description}
                name="description"
                onChange={handleChange}
                rows={5}
                className={classnames(errors.description && touched.description && 'is-invalid')}
              />
            </div>
            <label>Anexo</label>
            <Downloads files={values.attachments} name="attachments" />
            <hr />
            <div className="d-flex flex-column">
              <AssemblySubjects schema={subjectSchema} />

              <button type="submit" className="btn btn-block btn-primary mb-3">
                {buttonLabel}
              </button>
            </div>
          </form>
        </div>
      </>
    </FormikProvider>
  );
}

AssemblyPut.propTypes = {
  match: propTypes.object.isRequired,
  history: propTypes.object.isRequired,
};
